import React from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  DigitalQuestion,
  DigitalSubmission,
  QuestionTypeV2,
  RootState,
} from '../../redux/types';
import ContextQuestionInputForm from '../survey/questionnaire/ContextQuestionInputForm';
import { formatNumber } from '../../utilities/numberHelper';

const SectionGroup = styled(Box)`
  display: block;
  margin: 1px;
  width: 210mm;
`;

const SectionContainer = styled(Box)`
  &:nth-of-type(n+2){
    break-before: page;
    break-before: column;
    page-break-before: always;
  }
`;

type Props = {
  onRef: React.MutableRefObject<null>;
  trialId: number;
  enrollmentId: number;
}

const GetAnswer = (
  dq: DigitalQuestion,
  submissionObj: DigitalSubmission | undefined,
  locale: string,
) => {
  let answer;
  if (dq.type === QuestionTypeV2.NumberRange
    && submissionObj?.type === QuestionTypeV2.NumberRange) {
    if (submissionObj.answer === 'RTU' || submissionObj.answer === '-') {
      answer = submissionObj.answer;
    } else {
      answer = formatNumber(locale, submissionObj.answer);
    }
  }
  if (dq.type === QuestionTypeV2.SingleChoice
    && submissionObj?.type === QuestionTypeV2.SingleChoice) {
    const optionObj = dq.payload
      ?.find((v) => v.id === submissionObj.answer);
    answer = optionObj?.text;
  }
  if (dq.type === QuestionTypeV2.MultiChoice
    && submissionObj?.type === QuestionTypeV2.MultiChoice) {
    answer = dq.payload?.map((p) => (submissionObj
      .answer[p.id]
      ? <li key={p.id}>{p.text}</li> : undefined))
      .filter((a) => !!a);
    answer = <ul style={{ listStylePosition: 'inside', padding: 0, margin: 0 }}>{answer}</ul>;
  }
  if (dq.type === QuestionTypeV2.Ratio
    && submissionObj?.type === QuestionTypeV2.Ratio) {
    answer = submissionObj.answer.join(' : ');
  }
  if (dq.type === QuestionTypeV2.String
    && submissionObj?.type === QuestionTypeV2.String) {
    answer = submissionObj.answer.answer;
  }
  if (dq.type === QuestionTypeV2.SingleChoiceWithText
    && submissionObj?.type === QuestionTypeV2.SingleChoiceWithText) {
    const optionObj = dq
      .payload?.find((v) => v.id in submissionObj.answer);
    if (optionObj) {
      const val = submissionObj.answer[optionObj.id]?.text;
      answer = `${optionObj?.text} ${val ? ` (${val})` : ''}`;
    }
  }
  if (dq.type === QuestionTypeV2.MultiChoiceWithText
    && submissionObj?.type === QuestionTypeV2.MultiChoiceWithText) {
    answer = dq.payload?.map((p) => (
      submissionObj.answer[p.id]
        && submissionObj.answer[p.id].selected
        ? (
          <li key={p.id}>
            {p.text}
            {submissionObj.answer[p.id].text ? ` (${submissionObj.answer[p.id].text})` : ''}
          </li>
        ) : undefined))
      .filter((a) => !!a);
    answer = <ul style={{ listStylePosition: 'inside', padding: 0 }}>{answer}</ul>;
  }
  return answer;
};

const AnswerSummaryPrint: React.FC<Props> = ({
  onRef,
  trialId,
  enrollmentId,
}: Props) => {
  const locale = useSelector((state: RootState) => state.intl.locale);
  const sectionsAllIds = useSelector((state: RootState) => state.SurveyData.digitalSections.allIds);
  const sectionsById = useSelector((state: RootState) => state.SurveyData.digitalSections.byId);
  const questionsAllIds = useSelector(
    (state: RootState) => state.SurveyData.digitalQuestions.allIds,
  );
  const questionsById = useSelector((state: RootState) => state.SurveyData.digitalQuestions.byId);
  const casesAllIds = useSelector((state: RootState) => state.SurveyData.digitalCases.allIds);
  const casesById = useSelector((state: RootState) => state.SurveyData.digitalCases.byId);
  const submissionsMap = useSelector((state: RootState) => state.SurveyData.digitalSubmission.byId);

  const sections = sectionsAllIds.map((i) => sectionsById[i]);
  const getQuestions = (secId: number) => questionsAllIds.map((i) => questionsById[i])
    .filter((q) => q.digitalSectionId === secId && !q.isContextQuestion);
  const getContextQuestions = (secId: number) => questionsAllIds.map((i) => questionsById[i])
    .filter((q) => q.digitalSectionId === secId && q.isContextQuestion);
  const getCases = (secId: number) => casesAllIds.map((c) => casesById[c])
    .filter((c) => c.digitalSectionId === secId);

  const submissionsMapByQuestionAndCaseId = (secId: number) => {
    const submissionsBySection = Object.values(submissionsMap)
      .filter((s) => s.digitalSectionId === secId);
    const sDictionary: Record<number, Record<number, DigitalSubmission> | undefined> = {};
    submissionsBySection.forEach((ds) => {
      if (ds.digitalCaseId !== undefined) {
        sDictionary[ds.digitalQuestionId] = {
          ...sDictionary[ds.digitalQuestionId],
          [ds.digitalCaseId]: ds,
        };
      }
    });
    return sDictionary;
  };

  const selectContextSubmissionByQuestion = (secId: number) => {
    const ctxSubmissionsBySection = Object.values(submissionsMap)
      .filter((s) => s.digitalSectionId === secId && !s.digitalCaseId);
    return ctxSubmissionsBySection.reduce((acc, cur) => {
      acc[cur.digitalQuestionId] = cur;
      return acc;
    }, {} as Record<number, DigitalSubmission | undefined>);
  };

  if (!questionsAllIds.length || !casesAllIds.length || !sectionsAllIds.length) {
    return null;
  }
  return (
    <SectionGroup ref={onRef}>
      {
        sections.map((sec, idx) => (
          <SectionContainer key={sec.id} sx={idx > 0 ? { mt: 3 } : null}>
            <Typography variant="h4">{sec.name}</Typography>
            {
              getQuestions(sec.id).map((dq) => (
                <Box key={dq.id} sx={{ my: 4 }}>
                  <Typography fontWeight={500}>{dq.text}</Typography>
                  <TableContainer>
                    <Table size="small" sx={{ position: 'relative', overflow: 'auto' }}>
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{
                              left: 0,
                              width: 250,
                            }}
                          >
                            Cases
                          </TableCell>
                          <TableCell>
                            Answers
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {
                          getCases(sec.id).map((dc) => (
                            <React.Fragment key={dc.id}>
                              <TableRow>
                                <TableCell>
                                  {dc.name}
                                </TableCell>
                                <TableCell
                                  align="left"
                                  sx={{
                                    lineBreak: 'strict',
                                  }}
                                >
                                  {
                                    (() => {
                                      const submissionObj = submissionsMapByQuestionAndCaseId(
                                        sec.id,
                                      )[
                                        dq.id]?.[dc.id];
                                      const answer = GetAnswer(dq, submissionObj, locale);
                                      return (
                                        <span>
                                          {
                                            answer === undefined ? '-' : answer
                                          }
                                        </span>
                                      );
                                    })()
                                  }
                                </TableCell>
                              </TableRow>
                            </React.Fragment>
                          ))
                        }
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ))
            }
            <Box>
              <ContextQuestionInputForm
                trialId={Number(trialId)}
                sectionId={sec.id}
                enrollmentId={enrollmentId}
                questions={getContextQuestions(sec.id)}
                contextSubmissionByQuestionId={selectContextSubmissionByQuestion(sec.id)}
                addOrUpdateDigitalSubmission={() => undefined}
                summarizedView
              />
            </Box>
          </SectionContainer>
        ))
      }
    </SectionGroup>
  );
};

export default AnswerSummaryPrint;
